/* eslint-disable */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Fade,
  Divider,
  CardMedia,
  CircularProgress,
  Collapse,
  Stack,
} from "@mui/material";
import {
  LocationOn as MapPin,
  ZoomIn,
  ZoomOut,
  MyLocation,
  FilterList,
  Close as CloseIcon,
} from "@mui/icons-material";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { Wrapper } from "@googlemaps/react-wrapper";
import { useNavigate, useLocation } from "react-router-dom";
import { handleImageError, convertGoogleDriveUrl } from '../Constants/images';
import { buildPropertyImageAlt } from '../Utils/propertyImageAlt';
import { PropertyCard } from './PropertyCard';

// Google Maps API Key from environment variable
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyD7v7HXXdl77SmOEZC03SeBkJ_ybuTYDg8";

// Karnataka Districts with approximate center coordinates (31 districts - matching master data)
const KARNATAKA_DISTRICTS = {
  "Udupi": { lat: 13.3389, lng: 74.7451 },
  "Bagalkot": { lat: 16.1691, lng: 75.6615 },
  "Ballari": { lat: 15.1394, lng: 76.9214 },
  "Belagavi": { lat: 15.8497, lng: 74.4977 },
  "Bengaluru Rural": { lat: 13.2257, lng: 77.5750 },
  "Bengaluru Urban": { lat: 12.9716, lng: 77.5946 },
  "Bidar": { lat: 17.9104, lng: 77.5199 },
  "Chamarajanagar": { lat: 11.9236, lng: 76.9400 },
  "Chikkaballapur": { lat: 13.4355, lng: 77.7315 },
  "Chikkamagaluru": { lat: 13.3161, lng: 75.7720 },
  "Chitradurga": { lat: 13.9462, lng: 76.3985 },
  "Dakshina Kannada": { lat: 12.8438, lng: 75.2479 },
  "Davanagere": { lat: 14.4644, lng: 75.9218 },
  "Dharwad": { lat: 15.3647, lng: 75.1240 },
  "Gadag": { lat: 15.4166, lng: 75.6263 },
  "Hassan": { lat: 13.0068, lng: 76.1004 },
  "Haveri": { lat: 14.7951, lng: 75.3991 },
  "Kalaburagi": { lat: 17.3297, lng: 76.8343 },
  "Kodagu": { lat: 12.4244, lng: 75.7382 },
  "Kolar": { lat: 13.1326, lng: 78.1292 },
  "Koppal": { lat: 15.3547, lng: 76.1548 },
  "Mandya": { lat: 12.5242, lng: 76.8958 },
  "Mysuru": { lat: 12.2958, lng: 76.6394 },
  "Raichur": { lat: 16.2120, lng: 77.3439 },
  "Ramanagara": { lat: 12.7159, lng: 77.2814 },
  "Shivamogga": { lat: 13.9299, lng: 75.5681 },
  "Tumakuru": { lat: 13.3379, lng: 77.1173 },
  "Uttara Kannada": { lat: 14.7937, lng: 74.4892 },
  "Vijayapura": { lat: 16.8302, lng: 75.7100 },
  "Vijayanagara": { lat: 15.2993, lng: 76.3882 },
  "Yadgir": { lat: 16.7603, lng: 77.1381 },
};

// Udupi District Details - Taluks with their coordinates
const UDUPI_DISTRICT = {
  name: "Udupi",
  center: { lat: 13.3389, lng: 74.7451 },
  zoom: 11,
  taluks: {
    "Udupi": { lat: 13.3389, lng: 74.7451, zoom: 13 },
    "Kundapura": { lat: 13.6217, lng: 74.6917, zoom: 12 },
    "Karkala": { lat: 13.2128, lng: 74.9974, zoom: 12 },
    "Brahmavar": { lat: 13.4258, lng: 74.7458, zoom: 13 },
    "Byndoor": { lat: 13.8644, lng: 74.6356, zoom: 12 },
    "Hebri": { lat: 13.4167, lng: 75.0833, zoom: 12 },
    "Kaup": { lat: 13.2275, lng: 74.7494, zoom: 13 },
  },
};

// Karnataka state boundaries (approximate)
const KARNATAKA_BOUNDS = {
  center: { lat: 15.3173, lng: 75.7139 },
};

// Helper function to extract coordinates from Google Maps link
const extractCoordinatesFromMapLink = (mapLink) => {
  if (!mapLink) return null;
  try {
    // Pattern 1: Look for @lat,lng,zoom format
    const pattern1 = /@(-?\d+\.?\d*),(-?\d+\.?\d*),/;
    const match1 = mapLink.match(pattern1);
    if (match1) {
      return {
        lat: parseFloat(match1[1]),
        lng: parseFloat(match1[2]),
      };
    }

    // Pattern 2: Look for !3d and !4d parameters (alternative format)
    const pattern2 = /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/;
    const match2 = mapLink.match(pattern2);
    if (match2) {
      return {
        lat: parseFloat(match2[1]),
        lng: parseFloat(match2[2]),
      };
    }

    // Pattern 3: URL parameters format
    const url = new URL(mapLink);
    const latParam = url.searchParams.get('lat') || url.searchParams.get('latitude');
    const lngParam = url.searchParams.get('lng') || url.searchParams.get('longitude');
    if (latParam && lngParam) {
      return {
        lat: parseFloat(latParam),
        lng: parseFloat(lngParam),
      };
    }

    return null;
  } catch (error) {
    console.error('Error extracting coordinates from map link:', error);
    return null;
  }
};

// Enhanced coordinate parsing function - supports multiple formats
const parseCoordinates = (locationString) => {
  if (!locationString || typeof locationString !== 'string') {
    return null;
  }

  const location = locationString.trim();

  // Format 1: "12.9698° N, 77.7500° E" or "12.9698°N, 77.7500°E"
  const format1 = location.match(/(\d+\.?\d*)°?\s*N,?\s*(\d+\.?\d*)°?\s*E/i);
  if (format1) {
    return { lat: parseFloat(format1[1]), lng: parseFloat(format1[2]) };
  }

  // Format 2: "12.9698, 77.7500" (simple decimal)
  const format2 = location.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
  if (format2) {
    const lat = parseFloat(format2[1]);
    const lng = parseFloat(format2[2]);
    // Validate it's in India region
    if (lat >= 6 && lat <= 37 && lng >= 68 && lng <= 97) {
      return { lat, lng };
    }
  }

  // Format 3: Google Maps URL - extract coordinates
  const googleMapsUrl = location.match(/(?:maps[?@]|ll=|q=)(-?\d+\.?\d*),(-?\d+\.?\d*)/i);
  if (googleMapsUrl) {
    return { lat: parseFloat(googleMapsUrl[1]), lng: parseFloat(googleMapsUrl[2]) };
  }

  // Format 4: Google Maps embed iframe - extract coordinates
  const embedPattern = location.match(/!3d(-?\d+\.?\d*).*?!2d(-?\d+\.?\d*)|!2d(-?\d+\.?\d*).*?!3d(-?\d+\.?\d*)/);
  if (embedPattern) {
    if (embedPattern[1] && embedPattern[2]) {
      return { lat: parseFloat(embedPattern[1]), lng: parseFloat(embedPattern[2]) };
    }
    if (embedPattern[3] && embedPattern[4]) {
      return { lat: parseFloat(embedPattern[4]), lng: parseFloat(embedPattern[3]) };
    }
  }

  // Format 5: DMS format "12°58'17.8"N 77°35'40.6"E"
  const dmsPattern = location.match(/(\d+)°(\d+)'([\d.]+)"?\s*([NS]),?\s*(\d+)°(\d+)'([\d.]+)"?\s*([EW])/i);
  if (dmsPattern) {
    let lat = parseInt(dmsPattern[1]) + parseInt(dmsPattern[2])/60 + parseFloat(dmsPattern[3])/3600;
    let lng = parseInt(dmsPattern[5]) + parseInt(dmsPattern[6])/60 + parseFloat(dmsPattern[7])/3600;
    if (dmsPattern[4].toUpperCase() === 'S') lat = -lat;
    if (dmsPattern[8].toUpperCase() === 'W') lng = -lng;
    return { lat, lng };
  }

  return null;
};

// Validate coordinates are within Karnataka or nearby regions
const isValidKarnatakaCoordinate = (coords) => {
  if (!coords) return false;
  return (
    coords.lat >= 10 && coords.lat <= 20 &&
    coords.lng >= 72 && coords.lng <= 80
  );
};

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '700px',
  borderRadius: '12px',
};

// Map options
const mapOptions = {
  disableDefaultUI: false,
  zoomControl: false,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  styles: [],
};

const getFirstValue = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return value;
    }
  }
  return '';
};

const getSellerPhone = (property) => {
  const sellerType = String(
    getFirstValue(property.sellingAs, property.selling_as, 'Owner')
  ).toLowerCase();

  if (sellerType === 'broker') {
    return getFirstValue(
      property.brokerContact,
      property.broker_contact,
      property.broker_phone_number,
      ''
    );
  }

  return getFirstValue(
    property.ownerContact,
    property.owner_contact,
    property.owner_phone_number,
    ''
  );
};

const getSellerName = (property) => {
  const sellerType = String(
    getFirstValue(property.sellingAs, property.selling_as, 'Owner')
  ).toLowerCase();

  if (sellerType === 'broker') {
    return getFirstValue(property.broker, property.broker_name, '');
  }

  return getFirstValue(property.ownerName, property.owner_name, '');
};

const getSellerEmail = (property) => {
  const sellerType = String(
    getFirstValue(property.sellingAs, property.selling_as, 'Owner')
  ).toLowerCase();

  if (sellerType === 'broker') {
    return getFirstValue(
      property.brokerEmail,
      property.broker_email_id,
      property.broker_email,
      ''
    );
  }

  return getFirstValue(
    property.ownerEmail,
    property.owner_email_id,
    property.owner_email,
    ''
  );
};

const getPropertyIdentifier = (property) => {
  if (!property) return '';

  const directIdentifier =
    property.property_id ||
    property.propertyId ||
    property.id ||
    property.propertyCode ||
    property.property_code;

  if (directIdentifier) {
    return String(directIdentifier);
  }

  if (property.coords?.lat && property.coords?.lng) {
    return `${property.coords.lat},${property.coords.lng}`;
  }

  return String(property.title || property.propertyType || '');
};

function PropertyMapView({
  properties,
  onViewDetails,
  focusDistrict = null,
  isLoggedIn = false,
  user = null,
  renderPropertyCardPopup = false,
  showEditDelete = false,
  onEdit,
  onDelete,
  onUnlist,
  disableContactButton = false,
  useUpdateAsSecondaryAction = false,
  onUpdateVerification,
}) {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [mapType, setMapType] = useState("roadmap");
  const [mapInstance, setMapInstance] = useState(null);
  const [showDistrictFilter, setShowDistrictFilter] = useState(false);
  const [openContactPropertyId, setOpenContactPropertyId] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState(focusDistrict);
  const [shouldFitBounds, setShouldFitBounds] = useState(true);
  const [selectedTaluk, setSelectedTaluk] = useState(null);
  const mapRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const pendingContactPropertyId = location.state?.openContactPropertyId;
    if (!isLoggedIn || !pendingContactPropertyId) return;

    setOpenContactPropertyId(String(pendingContactPropertyId));
    navigate(location.pathname + location.search, {
      replace: true,
      state: {},
    });
  }, [isLoggedIn, location.pathname, location.search, location.state, navigate]);

  const handleContactSeller = (property) => {
    const propertyIdentifier = getPropertyIdentifier(property);

    if (!isLoggedIn) {
      navigate('/login', {
        state: {
          from: location.pathname + location.search,
          openContactPropertyId: propertyIdentifier,
        },
      });
      return;
    }

    setOpenContactPropertyId((prev) => (prev === propertyIdentifier ? '' : propertyIdentifier));
  };

  // Check if focusing on Udupi
  const isUdupiFocus = focusDistrict?.toLowerCase() === 'udupi' || selectedDistrict?.toLowerCase() === 'udupi';

  // Get initial map center and zoom based on focus
  const getInitialMapConfig = () => {
    if (isUdupiFocus) {
      return {
        center: UDUPI_DISTRICT.center,
        zoom: UDUPI_DISTRICT.zoom
      };
    }
    return {
      center: KARNATAKA_BOUNDS.center,
      zoom: 7
    };
  };

  const initialMapConfig = getInitialMapConfig();
  const [center, setCenter] = useState(initialMapConfig.center);
  const [zoom, setZoom] = useState(initialMapConfig.zoom);

  // Process properties with coordinates
  const propertiesWithCoordinates = useMemo(() => {
    return properties
      .map(property => {
        let coords = null;
        
        // Method 1a: geoLocation object (set by propertyTransform when location is a coordinate object)
        const geoLoc = property.geoLocation;
        if (geoLoc && typeof geoLoc === 'object') {
          if (geoLoc.mapLink) {
            coords = extractCoordinatesFromMapLink(geoLoc.mapLink);
          }
          if (!coords && geoLoc.latitude && geoLoc.longitude) {
            const lat = parseFloat(geoLoc.latitude);
            const lng = parseFloat(geoLoc.longitude);
            if (!isNaN(lat) && !isNaN(lng)) coords = { lat, lng };
          }
        }

        // Method 1b: location field when it is an object (raw backend / dashboard data)
        if (!coords && property.location && typeof property.location === 'object') {
          if (property.location.mapLink) {
            coords = extractCoordinatesFromMapLink(property.location.mapLink);
          }
          if (!coords && property.location.latitude && property.location.longitude) {
            const lat = parseFloat(property.location.latitude);
            const lng = parseFloat(property.location.longitude);
            if (!isNaN(lat) && !isNaN(lng)) coords = { lat, lng };
          }
        }
        
        // Method 2: flat latitude/longitude fields (populated by propertyTransform or raw backend)
        if (!coords && property.latitude && property.longitude) {
          const lat = parseFloat(property.latitude);
          const lng = parseFloat(property.longitude);
          if (!isNaN(lat) && !isNaN(lng)) coords = { lat, lng };
        }
        
        // Method 3: googleMapLocation / google_map_location string
        if (!coords) {
          coords = parseCoordinates(property.googleMapLocation || property.google_map_location);
        }
        
        if (coords && isValidKarnatakaCoordinate(coords)) {
          return { ...property, coords };
        }
        return null;
      })
      .filter(Boolean);
  }, [properties]);

  // Filter by district and taluk if selected
  const filteredProperties = useMemo(() => {
    let filtered = propertiesWithCoordinates;

    // Filter by district
    if (selectedDistrict) {
      filtered = filtered.filter(p =>
        p.district?.toLowerCase().includes(selectedDistrict.toLowerCase())
      );
    }

    // Filter by taluk (only when in Udupi focus mode)
    if (isUdupiFocus && selectedTaluk) {
      filtered = filtered.filter(p =>
        p.taluk?.toLowerCase().includes(selectedTaluk.toLowerCase())
      );
    }

    return filtered;
  }, [propertiesWithCoordinates, selectedDistrict, selectedTaluk, isUdupiFocus]);

  // Count stats
  const validCount = propertiesWithCoordinates.length;
  const invalidCount = properties.length - validCount;

  // Get all districts with property count (show all 31 districts)
  const districtStats = useMemo(() => {
    // Start with all districts from KARNATAKA_DISTRICTS with count 0
    const stats = {};
    Object.keys(KARNATAKA_DISTRICTS).forEach(district => {
      stats[district] = 0;
    });

    // Count properties per district
    propertiesWithCoordinates.forEach(p => {
      const district = p.district || 'Unknown';
      // Match district name (case-insensitive partial match)
      const matchedDistrict = Object.keys(KARNATAKA_DISTRICTS).find(
        d => d.toLowerCase() === district.toLowerCase() ||
             district.toLowerCase().includes(d.toLowerCase()) ||
             d.toLowerCase().includes(district.toLowerCase())
      );
      if (matchedDistrict) {
        stats[matchedDistrict] = (stats[matchedDistrict] || 0) + 1;
      } else {
        stats[district] = (stats[district] || 0) + 1;
      }
    });

    // Sort: districts with properties first, then alphabetically
    return Object.entries(stats).sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1]; // By count descending
      return a[0].localeCompare(b[0]); // Then alphabetically
    });
  }, [propertiesWithCoordinates]);

  // Get taluk stats for Udupi district
  const talukStats = useMemo(() => {
    if (!isUdupiFocus) return [];
    const stats = {};
    propertiesWithCoordinates
      .filter(p => p.district?.toLowerCase().includes('udupi'))
      .forEach(p => {
        const taluk = p.taluk || 'Unknown';
        stats[taluk] = (stats[taluk] || 0) + 1;
      });
    return Object.entries(stats).sort((a, b) => b[1] - a[1]);
  }, [propertiesWithCoordinates, isUdupiFocus]);

  // Helper function to get the property image
  const getPropertyImage = useCallback((property) => {
    const images = property.images || [];
    const imageArray = Array.isArray(images)
      ? images
      : (typeof images === 'string' ? (() => { try { return JSON.parse(images); } catch { return []; } })() : []);

    const firstImage = imageArray.length > 0 ? imageArray[0] : null;
    const imageUrl = firstImage || property.image || null;

    if (!imageUrl) {
      return null;
    }

    return convertGoogleDriveUrl(imageUrl);
  }, []);

  const formatPrice = (price) => {
    if (price == null || isNaN(price) || price < 0) return 'Price on Request';
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString()}`;
  };

  // Map control handlers
  const handleZoomIn = () => {
    if (mapInstance) {
      mapInstance.setZoom(mapInstance.getZoom() + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapInstance) {
      mapInstance.setZoom(mapInstance.getZoom() - 1);
    }
  };

  const handleCenterKarnataka = () => {
    setCenter(KARNATAKA_BOUNDS.center);
    setZoom(7);
    setShouldFitBounds(false);
  };

  // Center on Udupi district
  const handleCenterUdupi = () => {
    setCenter(UDUPI_DISTRICT.center);
    setZoom(UDUPI_DISTRICT.zoom);
    setSelectedDistrict('Udupi');
    setSelectedTaluk(null);
    setShouldFitBounds(false);
  };

  // Handle taluk click for Udupi
  const handleTalukClick = (talukName) => {
    setSelectedTaluk(talukName === selectedTaluk ? null : talukName);
    setShouldFitBounds(true);

    const talukCoords = UDUPI_DISTRICT.taluks[talukName];
    if (talukCoords) {
      setCenter({ lat: talukCoords.lat, lng: talukCoords.lng });
      setZoom(talukCoords.zoom || 13);
    }
  };

  const handleDistrictClick = (district) => {
    setSelectedDistrict(district === selectedDistrict ? null : district);
    setShouldFitBounds(true);

    const districtCoords = KARNATAKA_DISTRICTS[district];
    if (districtCoords) {
      setCenter(districtCoords);
      setZoom(11);
    }
  };

  // Auto-fit bounds when properties change
  useEffect(() => {
    if (mapInstance && shouldFitBounds && filteredProperties.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      filteredProperties.forEach(property => {
        bounds.extend({ lat: property.coords.lat, lng: property.coords.lng });
      });
      mapInstance.fitBounds(bounds);
      setShouldFitBounds(false);
    }
  }, [mapInstance, filteredProperties, shouldFitBounds]);

  // Custom marker icon with VR branding
  const getMarkerIcon = useCallback((property) => {
    // Check if Google Maps is loaded
    if (!window.google || !window.google.maps) {
      return null;
    }

    const size = 40;
    const pointerHeight = 12;
    const totalHeight = size + pointerHeight;
    const vrBlue = '#1976D2';
    const vrBlueDark = '#1565C0';
    const radius = size / 2 - 2;
    const propertyIdentifier = getPropertyIdentifier(property);

    const svg = `
      <svg width="${size}" height="${totalHeight}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="vrGradient-${propertyIdentifier}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:${vrBlue};stop-opacity:1" />
            <stop offset="100%" style="stop-color:${vrBlueDark};stop-opacity:1" />
          </linearGradient>
          <filter id="shadow-${propertyIdentifier}">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="0" dy="2" result="offsetblur"/>
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.3"/>
            </feComponentTransfer>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <!-- Pointer/Arrow below circle -->
        <polygon points="${size/2},${totalHeight} ${size/2 - 8},${size - 4} ${size/2 + 8},${size - 4}" fill="${vrBlueDark}" filter="url(#shadow-${propertyIdentifier})"/>
        <!-- Main circle -->
        <circle cx="${size/2}" cy="${size/2}" r="${radius}" fill="url(#vrGradient-${propertyIdentifier})"
          stroke="white" stroke-width="3" filter="url(#shadow-${propertyIdentifier})"/>
        <!-- VR Text -->
        <text x="${size/2}" y="${size/2 + 5}" font-size="14"
          font-weight="800" text-anchor="middle" fill="white" font-family="Arial Black, Arial, sans-serif">VR</text>
        ${property.featured ? `<circle cx="${size - 6}" cy="6" r="8" fill="#FFD700" stroke="white" stroke-width="2"/>
          <text x="${size - 6}" y="10" font-size="9" text-anchor="middle" fill="#333">★</text>` : ''}
      </svg>
    `;

    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(size, totalHeight),
      anchor: new window.google.maps.Point(size / 2, totalHeight),
    };
  }, []);

  const onLoad = useCallback((map) => {
    setMapInstance(map);
    mapRef.current = map;
  }, []);

  // Pre-compute marker icons once per property so marker props stay stable across renders.
  // Marker visuals do not change on click, so only selected property details are updated.
  const markerIconCache = useMemo(() => {
    if (!window.google?.maps) return {};
    const cache = {};
    filteredProperties.forEach(property => {
      const id = getPropertyIdentifier(property);
      cache[id] = {
        normal: getMarkerIcon(property),
      };
    });
    return cache;
  }, [filteredProperties, getMarkerIcon, getPropertyImage]);

  return (
    <Box sx={{
      position: 'relative',
      height: '700px',
      borderRadius: 3,
      overflow: 'hidden',
      boxShadow: 4,
    }}>
      {/* Top Controls Bar */}
      <Box sx={{
        position: 'absolute',
        top: 16,
        left: 16,
        right: 16,
        zIndex: 1000,
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        alignItems: 'flex-start',
      }}>
        {/* Map Type Toggle */}
        <Box sx={{
          display: 'flex',
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: 3,
          overflow: 'hidden',
        }}>
          <Button
            size="small"
            onClick={() => setMapType("roadmap")}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 0,
              bgcolor: mapType === "roadmap" ? 'primary.main' : 'white',
              color: mapType === "roadmap" ? 'white' : 'text.primary',
              '&:hover': { bgcolor: mapType === "roadmap" ? 'primary.dark' : 'grey.100' },
            }}
          >
            🗺️ Map
          </Button>
          <Button
            size="small"
            onClick={() => setMapType("satellite")}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 0,
              bgcolor: mapType === "satellite" ? 'success.main' : 'white',
              color: mapType === "satellite" ? 'white' : 'text.primary',
              '&:hover': { bgcolor: mapType === "satellite" ? 'success.dark' : 'grey.100' },
            }}
          >
            🛰️ Satellite
          </Button>
          <Button
            size="small"
            onClick={() => setMapType("hybrid")}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 0,
              bgcolor: mapType === "hybrid" ? 'info.main' : 'white',
              color: mapType === "hybrid" ? 'white' : 'text.primary',
              '&:hover': { bgcolor: mapType === "hybrid" ? 'info.dark' : 'grey.100' },
            }}
          >
            🌍 Hybrid
          </Button>
        </Box>

        {/* District Filter Toggle */}
        <Button
          size="small"
          variant={showDistrictFilter ? "contained" : "outlined"}
          onClick={() => setShowDistrictFilter(!showDistrictFilter)}
          startIcon={<FilterList />}
          sx={{
            bgcolor: showDistrictFilter ? 'primary.main' : 'white',
            boxShadow: 3,
          }}
        >
          Districts
        </Button>

        {/* Udupi Focus Button */}
        <Button
          size="small"
          variant={isUdupiFocus ? "contained" : "outlined"}
          color={isUdupiFocus ? "success" : "primary"}
          onClick={handleCenterUdupi}
          sx={{
            bgcolor: isUdupiFocus ? 'success.main' : 'white',
            boxShadow: 3,
            fontWeight: 600,
          }}
        >
          📍 Udupi District
        </Button>

        {/* Stats Card */}
        <Box sx={{
          bgcolor: 'white',
          borderRadius: 2,
          px: 2,
          py: 1,
          boxShadow: 3,
          ml: 'auto',
        }}>
          <Typography variant="body2" fontWeight={600} color="primary.main">
            {filteredProperties.length} Properties on Map
            {isUdupiFocus && ` (Udupi${selectedTaluk ? ` - ${selectedTaluk}` : ''})`}
          </Typography>
          {invalidCount > 0 && (
            <Typography variant="caption" color="warning.main">
              {invalidCount} without coordinates
            </Typography>
          )}
        </Box>
      </Box>

      {/* District Filter Panel */}
      <Fade in={showDistrictFilter}>
        <Box sx={{
          position: 'absolute',
          top: 70,
          left: 16,
          zIndex: 1000,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: 4,
          p: 2,
          maxHeight: '400px',
          overflowY: 'auto',
          width: 250,
          display: showDistrictFilter ? 'block' : 'none',
        }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Filter by District
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {selectedDistrict && (
            <Chip
              label={`${selectedDistrict} ✕`}
              size="small"
              color="primary"
              onClick={() => { setSelectedDistrict(null); setSelectedTaluk(null); }}
              sx={{ mb: 1 }}
            />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {districtStats.map(([district, count]) => (
              <Button
                key={district}
                size="small"
                variant={selectedDistrict === district ? "contained" : "text"}
                onClick={() => handleDistrictClick(district)}
                sx={{
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  px: 1,
                }}
              >
                <span>{district}</span>
                <Chip label={count} size="small" sx={{ ml: 1, height: 20 }} />
              </Button>
            ))}
          </Box>

          {/* Udupi Taluks Filter */}
          {isUdupiFocus && talukStats.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Udupi Taluks
              </Typography>
              {selectedTaluk && (
                <Chip
                  label={`${selectedTaluk} ✕`}
                  size="small"
                  color="secondary"
                  onClick={() => setSelectedTaluk(null)}
                  sx={{ mb: 1 }}
                />
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {talukStats.map(([taluk, count]) => (
                  <Button
                    key={taluk}
                    size="small"
                    variant={selectedTaluk === taluk ? "contained" : "text"}
                    color="secondary"
                    onClick={() => handleTalukClick(taluk)}
                    sx={{
                      justifyContent: 'space-between',
                      textTransform: 'none',
                      px: 1,
                    }}
                  >
                    <span>{taluk}</span>
                    <Chip label={count} size="small" color="secondary" variant="outlined" sx={{ ml: 1, height: 20 }} />
                  </Button>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Fade>

      {/* Legend */}
      <Box sx={{
        position: 'absolute',
        bottom: 80,
        left: 16,
        zIndex: 1000,
        bgcolor: 'rgba(255,255,255,0.95)',
        borderRadius: 2,
        p: 2,
        boxShadow: 3,
        minWidth: 160,
      }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Map Legend
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1976D2, #1565C0)',
              border: '2px solid white',
              boxShadow: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 800,
              fontSize: '10px',
              fontFamily: 'Arial Black, Arial, sans-serif',
            }}>VR</Box>
            <Typography variant="caption">Property Location</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1976D2, #1565C0)',
              border: '2px solid #FFD700',
              boxShadow: '0 0 8px rgba(255,215,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 800,
              fontSize: '10px',
              fontFamily: 'Arial Black, Arial, sans-serif',
            }}>VR</Box>
            <Typography variant="caption">Selected Property</Typography>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Click marker for details
        </Typography>
      </Box>

      {/* Map Controls */}
      <Box sx={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}>
        <Tooltip title="Zoom In" placement="left">
          <IconButton
            onClick={handleZoomIn}
            sx={{
              bgcolor: 'rgba(255,255,255,0.95)',
              boxShadow: 3,
              '&:hover': { bgcolor: 'white' },
            }}
          >
            <ZoomIn />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out" placement="left">
          <IconButton
            onClick={handleZoomOut}
            sx={{
              bgcolor: 'rgba(255,255,255,0.95)',
              boxShadow: 3,
              '&:hover': { bgcolor: 'white' },
            }}
          >
            <ZoomOut />
          </IconButton>
        </Tooltip>
        <Tooltip title="Center Karnataka" placement="left">
          <IconButton
            onClick={handleCenterKarnataka}
            sx={{
              bgcolor: 'rgba(255,255,255,0.95)',
              boxShadow: 3,
              '&:hover': { bgcolor: 'white' },
            }}
          >
            <MyLocation />
          </IconButton>
        </Tooltip>
      </Box>

      {/* No Properties Message */}
      {validCount === 0 && (
        <Box sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
        }}>
          <Card sx={{ maxWidth: 400, textAlign: 'center', p: 4 }}>
            <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>🗺️</Typography>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              No Properties on Map
            </Typography>
            <Typography color="text.secondary">
              {properties.length === 0
                ? "No properties match your filters."
                : "Properties don't have valid location coordinates. Add Google Map locations when listing properties."}
            </Typography>
          </Card>
        </Box>
      )}

      {/* Google Map - Wrapper loading flow aligned with property management */}
      <Wrapper
        apiKey={GOOGLE_MAPS_API_KEY}
        render={(status) => {
          if (status === 'LOADING') {
            return (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: 400,
                  bgcolor: 'grey.100',
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Loading Map...
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                  Please wait while the map loads
                </Typography>
              </Box>
            );
          }

          if (status === 'FAILURE') {
            return (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: 400,
                  bgcolor: 'grey.100',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" color="error">
                  Error loading Google Maps
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Please check your internet connection and try again
                </Typography>
              </Box>
            );
          }

          return (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={zoom}
              onLoad={onLoad}
              options={{
                ...mapOptions,
                mapTypeId: mapType,
              }}
              onClick={() => setSelectedProperty(null)}
            >
          {/* Property Markers */}
          {filteredProperties.map((property) => {
            const propertyIdentifier = getPropertyIdentifier(property);
            const selectedPropertyIdentifier = getPropertyIdentifier(selectedProperty);
            const isThisSelected = selectedPropertyIdentifier === propertyIdentifier;
            const cached = markerIconCache[propertyIdentifier];
            const markerIcon = cached?.normal;
            const isPropertyOwner = isLoggedIn && user && property.user_id && (parseInt(property.user_id) === parseInt(user.id));
            const contactOpen = openContactPropertyId === propertyIdentifier;
            // const infoSellerPhone = getSellerPhone(property);
            // const infoSellerName = getSellerName(property);
            const infoSellerPhone = "9900872434";
            const infoSellerName = "Prakash";
            const infoSellerEmail = getSellerEmail(property);

            return markerIcon ? (
              <Marker
                key={propertyIdentifier}
                position={{ lat: property.coords.lat, lng: property.coords.lng }}
                icon={markerIcon}
                onClick={() => {
                  if (selectedPropertyIdentifier !== propertyIdentifier) {
                    setSelectedProperty(property);
                  }
                }}
              >
                {isThisSelected && (
                  <InfoWindow onCloseClick={() => setSelectedProperty(null)}>
                    {renderPropertyCardPopup ? (
                      <Box sx={{ width: 340, maxWidth: '90vw' }}>
                        <PropertyCard
                          property={property}
                          onViewDetails={onViewDetails}
                          isLoggedIn={isLoggedIn}
                          user={user}
                          showEditDelete={showEditDelete}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          onUnlist={onUnlist}
                          disableContactButton={disableContactButton}
                          useUpdateAsSecondaryAction={useUpdateAsSecondaryAction}
                          onUpdateVerification={onUpdateVerification}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ width: 260, maxWidth: 260 }}>
                        {getPropertyImage(property) ? (
                          <CardMedia
                            component="img"
                            image={getPropertyImage(property)}
                            alt={buildPropertyImageAlt(property, { variant: 'listing' })}
                            onError={handleImageError}
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                            sx={{
                              height: 120,
                              width: '100%',
                              objectFit: 'cover',
                              borderRadius: 1,
                              mb: 1,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 120,
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.100',
                              color: 'text.secondary',
                              fontWeight: 500,
                              borderRadius: 1,
                              mb: 1,
                            }}
                          >
                            No image available
                          </Box>
                        )}
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ mb: 0.5 }}>
                          {property.propertyType} - {property.propertySubType || property.property_sub_type}
                        </Typography>
                        <Typography variant="h6" color="primary.main" fontWeight={700} sx={{ mb: 0.5 }}>
                          {formatPrice(
                            getFirstValue(
                              property.estimatedCurrentValue,
                              property.estimated_current_value,
                              property.expectedPrice,
                              property.expected_price,
                              0
                            )
                          )}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <MapPin sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {property.district}{property.taluk ? `, ${property.taluk}` : ''}
                          </Typography>
                        </Box>
                        {(property.bedroom_count || property.bedrooms || property.bathrooms || property.carpetArea || property.carpet_area) && (
                          <Box sx={{ display: 'flex', gap: 1.5, mb: 1, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
                            {(property.bedroom_count || property.bedrooms || 0) > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                🛏 <strong>{property.bedroom_count || property.bedrooms}</strong> Beds
                              </Typography>
                            )}
                            {(property.bathrooms || 0) > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                🚿 <strong>{property.bathrooms}</strong> Baths
                              </Typography>
                            )}
                            {(property.carpetArea || property.carpet_area || 0) > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                📐 <strong>{(property.carpetArea || property.carpet_area).toLocaleString()}</strong> sqft
                              </Typography>
                            )}
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          {!isPropertyOwner && (
                            <Button
                              fullWidth
                              size="small"
                              variant={contactOpen ? 'contained' : 'outlined'}
                              onClick={(e) => { e.stopPropagation(); handleContactSeller(property); }}
                              sx={{
                                borderRadius: 1.5,
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                py: 0.75,
                              }}
                            >
                              {contactOpen ? 'Hide Contact' : 'Contact'}
                            </Button>
                          )}
                          <Button
                            fullWidth
                            size="small"
                            variant="contained"
                            onClick={() => onViewDetails(property)}
                            sx={{
                              borderRadius: 1.5,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              py: 0.75,
                            }}
                          >
                            View Details
                          </Button>
                        </Box>
                        {!isPropertyOwner && (
                          <Collapse in={contactOpen}>
                            <Box
                              sx={{
                                mt: 1,
                                p: 1.5,
                                bgcolor: 'grey.50',
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: 'grey.200',
                              }}
                            >
                              {!infoSellerName && !infoSellerEmail && !infoSellerPhone ? (
                                <Typography variant="caption" color="text.secondary">
                                  No contact information available
                                </Typography>
                              ) : (
                                <Stack spacing={0.5}>
                                  {infoSellerName && (
                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                      {infoSellerName}
                                    </Typography>
                                  )}
                                  {infoSellerEmail && (
                                    <Typography
                                      component="a"
                                      href={`mailto:${infoSellerEmail}`}
                                      variant="caption"
                                      sx={{
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                    >
                                      {infoSellerEmail}
                                    </Typography>
                                  )}
                                  {infoSellerPhone && (
                                    <Typography
                                      component="a"
                                      href={`tel:${infoSellerPhone}`}
                                      variant="caption"
                                      sx={{
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                    >
                                      {infoSellerPhone}
                                    </Typography>
                                  )}
                                </Stack>
                              )}
                            </Box>
                          </Collapse>
                        )}
                      </Box>
                    )}
                  </InfoWindow>
                )}
              </Marker>
            ) : null;
          })}
            </GoogleMap>
          );
        }}
      />
    </Box>
  );
}

export default PropertyMapView;
